// src/components/GiftVoucher.js
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { QRCodeCanvas } from "qrcode.react";

import Navbar from "./Navbar";
import Footer from "./Footer";

import "./GiftVoucher.css";

// Language-specific voucher images
import voucherImageCZ from "../assets/gift-voucher_cz.jpg";
import voucherImageEN from "../assets/gift-voucher_eng.jpg";
import voucherImageDE from "../assets/gift-voucher_de.jpg";

const GiftVoucher = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [ready, setReady] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");

  // same lang-from-URL logic as PrivacyPolicy
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lang = params.get("lang");

    if (lang && lang !== i18n.language) {
      i18n.changeLanguage(lang).then(() => {
        setReady(true);
      });
    } else {
      setReady(true);
    }
  }, [location, i18n]);

  if (!ready) return null;

  const faqItems = t("giftVoucher.faq.items", { returnObjects: true });

  // -------- helpers --------

  // Shorter order number: GV-YYMM-HHMMSS
  const generateOrderNumber = () => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");

    return `GV-${yy}${mm}-${hh}${min}${ss}`;
  };

  const generateVoucherCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "OS-";
    for (let i = 0; i < 8; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
      if (i === 3) code += "-";
    }
    return code;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);

    const form = e.target;
    const data = new FormData(form);

    const buyerName = data.get("buyerName") || "";
    const email = data.get("buyerEmail") || "";
    const phone = data.get("buyerPhone") || "";
    const recipientName = data.get("recipientName") || "";
    const value = data.get("value") || "";
    const message = data.get("message") || "";
    const invoiceDetails = data.get("invoiceDetails") || "";
    const lang = i18n.language;

    const newOrderNumber = generateOrderNumber();
    const newVoucherCode = generateVoucherCode();

    setOrderNumber(newOrderNumber);
    setAmount(value);
    setBuyerEmail(email);

    // EmailJS config – set these in your .env
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const TEMPLATE_CUSTOMER =
      process.env.REACT_APP_EMAILJS_GV_CUSTOMER_TEMPLATE_ID;
    const TEMPLATE_OWNER = process.env.REACT_APP_EMAILJS_GV_OWNER_TEMPLATE_ID;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    const paymentBankAccount = t("giftVoucher.thankYou.bankAccount");
    const paymentNote = `${t(
      "giftVoucher.thankYou.paymentNote"
    )} ${newOrderNumber}`;

    // email to you (owner) – you still get voucher_code, language etc.
    const ownerParams = {
      order_number: newOrderNumber,
      voucher_code: newVoucherCode,
      lang,
      buyer_name: buyerName,
      buyer_email: email,
      buyer_phone: phone,
      recipient_name: recipientName,
      value,
      message,
      invoice_details: invoiceDetails
    };

    // email to customer – no voucher_code here
    const customerParams = {
      order_number: newOrderNumber,
      lang,
      buyer_name: buyerName,
      buyer_email: email,
      value,
      payment_bank_account: paymentBankAccount,
      payment_note: paymentNote
    };

    try {
      await Promise.all([
        emailjs.send(SERVICE_ID, TEMPLATE_OWNER, ownerParams, PUBLIC_KEY),
        emailjs.send(SERVICE_ID, TEMPLATE_CUSTOMER, customerParams, PUBLIC_KEY)
      ]);

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("EmailJS error", err);
      alert(
        "Something went wrong while sending your request. Please try again or contact me directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- QR Platba / SPAYD string ----------

  // IBAN from translations (add giftVoucher.thankYou.iban)
  const iban = t("giftVoucher.thankYou.iban"); // e.g. "CZ1234567890123456789012"

  const numericAmount = amount ? Number(amount) : 0;
  const vs = orderNumber.replace(/\D/g, "").slice(-10);
  const messageForPayment = `DARKOVY POUKAZ ${orderNumber}`;

  const qrText =
    submitted && iban && numericAmount > 0
      ? `SPD*1.0*ACC:${iban}*AM:${numericAmount.toFixed(
          2
        )}*CC:CZK*MSG:${messageForPayment}*X-VS:${vs}`
      : "";

  // ---------- Language-specific voucher image ----------
  const currentLang = (i18n.language || "").toLowerCase();
  let voucherImage = voucherImageEN;

  if (currentLang.startsWith("cz")) {
    voucherImage = voucherImageCZ;
  } else if (currentLang.startsWith("de")) {
    voucherImage = voucherImageDE;
  } else {
    voucherImage = voucherImageEN;
  }

  return (
    <>
      <Navbar />

      <main className="gift-voucher">
        {/* HERO: left text+image / right form card */}
        <section className="gift-voucher__hero">
          <div className="container gift-voucher__hero-inner">
            {/* LEFT COLUMN */}
            <div className="gift-voucher__left">
              <div className="gift-voucher__intro-block">
                <h2 className="gift-voucher__hero-title">
                  {t("giftVoucher.title")}
                </h2>
                <p className="gift-voucher__hero-text">
                  {t("giftVoucher.intro")}
                </p>
              </div>

              <div className="gift-voucher__image-wrapper">
                <img
                  src={voucherImage}
                  alt={t("giftVoucher.imageAlt")}
                  className="gift-voucher__image"
                />
              </div>
            </div>

            {/* RIGHT COLUMN – FORM / THANK YOU CARD */}
            <div className="gift-voucher__form-wrapper">
              {!submitted ? (
                <>
                  {/* Only heading inside the card */}
                  <h1 className="gift-voucher__title">
                    {t("giftVoucher.formTitle")}
                  </h1>

                  <form
                    className="gift-voucher__form"
                    name="gift-voucher"
                    onSubmit={handleSubmit}
                  >
                    <div className="gift-voucher__field">
                      <label htmlFor="buyerName">
                        {t("giftVoucher.form.buyerNameLabel")}*
                      </label>
                      <input
                        id="buyerName"
                        name="buyerName"
                        type="text"
                        placeholder={t(
                          "giftVoucher.form.buyerNamePlaceholder"
                        )}
                        required
                      />
                    </div>

                    <div className="gift-voucher__field">
                      <label htmlFor="buyerEmail">
                        {t("giftVoucher.form.buyerEmailLabel")}*
                      </label>
                      <input
                        id="buyerEmail"
                        name="buyerEmail"
                        type="email"
                        placeholder={t(
                          "giftVoucher.form.buyerEmailPlaceholder"
                        )}
                        required
                      />
                    </div>

                    <div className="gift-voucher__field">
                      <label htmlFor="buyerPhone">
                        {t("giftVoucher.form.buyerPhoneLabel")}*
                      </label>
                      <input
                        id="buyerPhone"
                        name="buyerPhone"
                        type="tel"
                        placeholder={t(
                          "giftVoucher.form.buyerPhonePlaceholder"
                        )}
                        required
                      />
                    </div>

                    {/* invoice details field */}
                    <div className="gift-voucher__field">
                      <label htmlFor="invoiceDetails">
                        {t("giftVoucher.form.invoiceDetailsLabel")}
                      </label>
                      <textarea
                        id="invoiceDetails"
                        name="invoiceDetails"
                        rows="3"
                        placeholder={t(
                          "giftVoucher.form.invoiceDetailsPlaceholder"
                        )}
                      />
                    </div>

                    <div className="gift-voucher__field">
                      <label htmlFor="recipientName">
                        {t("giftVoucher.form.recipientNameLabel")}
                      </label>
                      <input
                        id="recipientName"
                        name="recipientName"
                        type="text"
                        placeholder={t(
                          "giftVoucher.form.recipientNamePlaceholder"
                        )}
                      />
                    </div>

                    <div className="gift-voucher__field">
                      <label htmlFor="value">
                        {t("giftVoucher.form.valueLabel")}
                      </label>
                      <select id="value" name="value" defaultValue="">
                        <option value="" disabled>
                          {t("giftVoucher.form.valuePlaceholder")}
                        </option>
                        <option value="5000">
                          {t("giftVoucher.form.valueOption1")}
                        </option>
                        <option value="10000">
                          {t("giftVoucher.form.valueOption2")}
                        </option>
                        <option value="15000">
                          {t("giftVoucher.form.valueOption3")}
                        </option>
                      </select>
                    </div>

                    <div className="gift-voucher__field">
                      <label htmlFor="message">
                        {t("giftVoucher.form.messageLabel")}
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows="4"
                        placeholder={t(
                          "giftVoucher.form.messagePlaceholder"
                        )}
                      />
                    </div>

                    <p className="gift-voucher__hint">
                      {t("giftVoucher.form.hint")}
                    </p>

                    <p className="gift-voucher__hint">
                      {t("giftVoucher.form.cancellationNoticePrefix")}
                      <a
                        href={`/storno?lang=${i18n.language}`}
                        className="gift-voucher__storno-link"
                      >
                        {t("giftVoucher.form.cancellationNoticeLink")}
                      </a>
                      {t("giftVoucher.form.cancellationNoticeSuffix")}
                    </p>

                    <button
                      type="submit"
                      className="gift-voucher__submit"
                      disabled={submitting}
                    >
                      {submitting
                        ? t("giftVoucher.form.submitting")
                        : t("giftVoucher.form.submit")}
                    </button>
                  </form>
                </>
              ) : (
                <>
                  <h1 className="gift-voucher__title">
                    {t("giftVoucher.thankYou.title")}
                  </h1>
                  <p className="gift-voucher__intro">
                    {t("giftVoucher.thankYou.intro")}
                  </p>

                  <div className="gift-voucher__summary">
                    <p>
                      <strong>
                        {t("giftVoucher.thankYou.orderNumberLabel")}:
                      </strong>{" "}
                      {orderNumber}
                    </p>

                    {/* Voucher code not shown to customer */}

                    <p>
                      <strong>
                        {t("giftVoucher.thankYou.amountLabel")}:
                      </strong>{" "}
                      {amount || "TBD"} CZK
                    </p>
                    <p>
                      <strong>
                        {t("giftVoucher.thankYou.bankAccountLabel")}:
                      </strong>{" "}
                      {t("giftVoucher.thankYou.bankAccount")}
                    </p>
                    <p>
                      <strong>
                        {t("giftVoucher.thankYou.paymentNoteLabel")}:
                      </strong>{" "}
                      {t("giftVoucher.thankYou.paymentNote")} {orderNumber}
                    </p>
                  </div>

                  <div className="gift-voucher__qr">
                    <h3>{t("giftVoucher.thankYou.qrTitle")}</h3>

                    <div
                      style={{
                        backgroundColor: "#111",
                        padding: "12px",
                        borderRadius: "16px",
                        display: "inline-block"
                      }}
                    >
                      {qrText && (
                        <QRCodeCanvas
                          value={qrText}
                          size={170}
                          fgColor="#FFFFFF"
                          bgColor="transparent"
                          includeMargin={false}
                        />
                      )}
                    </div>
                  </div>

                  <p className="gift-voucher__hint">
                    {t("giftVoucher.thankYou.emailInfo")} {buyerEmail}
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* FAQ – full-width container below */}
        <section className="gift-voucher__faq">
          <h2 className="gift-voucher__faq-title">
            {t("giftVoucher.faq.title")}
          </h2>
          <p className="gift-voucher__faq-intro">
            {t("giftVoucher.faq.intro")}
          </p>

          <div className="gift-voucher__faq-list">
            {faqItems.map((item, index) => (
              <details key={index} className="gift-voucher__faq-item">
                <summary>{item.q}</summary>
                <div className="gift-voucher__faq-body">
                  <p>{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default GiftVoucher;
